import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, TrashIcon, TagIcon } from '@heroicons/react/24/outline';
import {
  PriceList,
  CreatePriceListData,
  UpdatePriceListData,
  PriceListType,
  PriceListStatus,
  PriceType,
} from '../../types/priceList';
import { inventoryService } from '../../services/inventory.service';
import { useActiveCustomers } from '../../hooks/useCustomers';
import {
  Field,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface PriceListFormProps {
  // When set, the form edits this price list; otherwise it creates a new one
  priceList?: PriceList | null;
  onSubmit: (data: CreatePriceListData | UpdatePriceListData) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

interface ItemForm {
  key: string;
  productId: string;
  priceType: PriceType;
  basePrice: string;
  percentage: string;
  finalPrice: string;
  minimumQuantity: string;
  maximumQuantity: string;
  additionalDiscountPercent: string;
}

export const PRICE_LIST_TYPE_LABELS: Record<PriceListType, string> = {
  [PriceListType.STANDARD]: 'Standard',
  [PriceListType.CUSTOMER_SPECIFIC]: 'Customer Specific',
  [PriceListType.PROMOTIONAL]: 'Promotional',
  [PriceListType.QUANTITY_BASED]: 'Quantity Based',
};

export const PRICE_TYPE_LABELS: Record<PriceType, string> = {
  [PriceType.FIXED]: 'Fixed price',
  [PriceType.PERCENTAGE_MARKUP]: '% markup on base',
  [PriceType.PERCENTAGE_DISCOUNT]: '% discount on base',
};

// A date in YYYY-MM-DD for date inputs
const toDateInput = (value?: string | null) => (value ? String(value).slice(0, 10) : '');

const todayInputValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

let itemKey = 0;
const newItem = (): ItemForm => ({
  key: `item-${++itemKey}`,
  productId: '',
  priceType: PriceType.FIXED,
  basePrice: '',
  percentage: '',
  finalPrice: '',
  minimumQuantity: '1',
  maximumQuantity: '',
  additionalDiscountPercent: '',
});

const toNumber = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// The final price of an item, the same way the server calculates it
const computeFinalPrice = (item: ItemForm) => {
  const base = toNumber(item.basePrice);
  const percentage = toNumber(item.percentage);
  if (item.priceType === PriceType.PERCENTAGE_MARKUP) return base * (1 + percentage / 100);
  if (item.priceType === PriceType.PERCENTAGE_DISCOUNT) return base * (1 - percentage / 100);
  return item.finalPrice === '' ? base : toNumber(item.finalPrice);
};

const formatCurrency = (amount: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(Number.isFinite(amount) ? amount : 0);

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, title, description, action, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
          {step}
        </span>
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const sectionGrid = 'grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2';

const PriceListForm: React.FC<PriceListFormProps> = ({ priceList, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!priceList;

  const [form, setForm] = useState({
    code: priceList?.code || '',
    name: priceList?.name || '',
    description: priceList?.description || '',
    type: priceList?.type || PriceListType.STANDARD,
    // An expired list is shown as inactive in the form; saving a new end date revives it
    status:
      priceList?.status && priceList.status !== PriceListStatus.EXPIRED
        ? priceList.status
        : priceList
          ? PriceListStatus.INACTIVE
          : PriceListStatus.DRAFT,
    customerId: priceList?.customerId || '',
    validFrom: toDateInput(priceList?.validFrom) || todayInputValue(),
    validTo: toDateInput(priceList?.validTo),
    currency: priceList?.currency || 'INR',
    priority: String(priceList?.priority ?? 1),
    globalDiscountPercent: priceList?.globalDiscountPercent ? String(Number(priceList.globalDiscountPercent)) : '',
  });

  const [items, setItems] = useState<ItemForm[]>(() =>
    (priceList?.items || []).map((item) => ({
      key: `item-${++itemKey}`,
      productId: item.productId,
      priceType: item.priceType,
      basePrice: String(Number(item.basePrice)),
      percentage: item.percentage != null ? String(Number(item.percentage)) : '',
      finalPrice: item.finalPrice != null ? String(Number(item.finalPrice)) : '',
      minimumQuantity: String(Number(item.minimumQuantity ?? 1)),
      maximumQuantity: item.maximumQuantity != null ? String(Number(item.maximumQuantity)) : '',
      additionalDiscountPercent: Number(item.additionalDiscountPercent) ? String(Number(item.additionalDiscountPercent)) : '',
    })),
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => inventoryService.getProducts(),
  });
  const { customers } = useActiveCustomers();

  const isCustomerSpecific = form.type === PriceListType.CUSTOMER_SPECIFIC;

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const setItemField = (index: number, name: keyof ItemForm, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, [name]: value };

        if (name === 'productId') {
          // Start from the product's selling price
          const product = products.find((p) => p.id === value);
          if (product) {
            next.basePrice = String(Number(product.price) || 0);
            if (next.priceType === PriceType.FIXED) {
              next.finalPrice = next.basePrice;
            }
          }
        }
        if (name === 'priceType' && value === PriceType.FIXED && next.finalPrice === '') {
          next.finalPrice = next.basePrice;
        }
        return next;
      }),
    );
    const errorKey = `item_${index}_${name}`;
    if (errors[errorKey] || errors.items) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', items: '' }));
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};

    if (!form.code.trim()) next.code = 'Code is required';
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.description.trim()) next.description = 'Description is required';
    if (!form.validFrom) next.validFrom = 'Valid from date is required';
    if (form.validFrom && form.validTo && form.validTo < form.validFrom) {
      next.validTo = 'Valid to cannot be before valid from';
    }
    if (isCustomerSpecific && !form.customerId) next.customerId = 'Choose the customer this list is for';
    if (!Number.isInteger(Number(form.priority)) || Number(form.priority) < 1) {
      next.priority = 'Priority must be a whole number of 1 or more';
    }
    const globalDiscount = toNumber(form.globalDiscountPercent);
    if (globalDiscount < 0 || globalDiscount > 100) {
      next.globalDiscountPercent = 'Discount must be between 0 and 100';
    }
    if (form.status === PriceListStatus.ACTIVE && items.length === 0) {
      next.items = 'An active price list needs at least one product price';
    }

    items.forEach((item, index) => {
      if (!item.productId) next[`item_${index}_productId`] = 'Product is required';
      if (item.basePrice === '' || toNumber(item.basePrice) <= 0) {
        next[`item_${index}_basePrice`] = 'Enter a base price above 0';
      }
      if (item.priceType === PriceType.FIXED) {
        if (item.finalPrice === '' || toNumber(item.finalPrice) <= 0) {
          next[`item_${index}_finalPrice`] = 'Enter a final price above 0';
        }
      } else {
        const percentage = toNumber(item.percentage);
        if (item.percentage === '' || percentage <= 0) {
          next[`item_${index}_percentage`] = 'Enter a percentage above 0';
        } else if (item.priceType === PriceType.PERCENTAGE_DISCOUNT && percentage > 100) {
          next[`item_${index}_percentage`] = 'A discount cannot exceed 100%';
        }
      }
      const minimum = toNumber(item.minimumQuantity);
      if (item.minimumQuantity === '' || minimum < 1) {
        next[`item_${index}_minimumQuantity`] = 'Must be at least 1';
      }
      if (item.maximumQuantity !== '' && toNumber(item.maximumQuantity) < minimum) {
        next[`item_${index}_maximumQuantity`] = 'Cannot be below the minimum';
      }
      const extra = toNumber(item.additionalDiscountPercent);
      if (extra < 0 || extra > 100) {
        next[`item_${index}_additionalDiscountPercent`] = 'Must be between 0 and 100';
      }
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: UpdatePriceListData = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      status: form.status,
      validFrom: form.validFrom,
      validTo: form.validTo || (isEdit ? null : undefined),
      currency: form.currency,
      customerId: isCustomerSpecific ? form.customerId : isEdit ? null : undefined,
      priority: Number(form.priority),
      globalDiscountPercent: toNumber(form.globalDiscountPercent),
      items: items.map((item) => ({
        productId: item.productId,
        priceType: item.priceType,
        basePrice: toNumber(item.basePrice),
        ...(item.priceType === PriceType.FIXED
          ? { finalPrice: toNumber(item.finalPrice) }
          : { percentage: toNumber(item.percentage) }),
        minimumQuantity: toNumber(item.minimumQuantity),
        ...(item.maximumQuantity !== '' ? { maximumQuantity: toNumber(item.maximumQuantity) } : {}),
        additionalDiscountPercent: toNumber(item.additionalDiscountPercent),
      })),
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const productCount = new Set(items.map((item) => item.productId).filter(Boolean)).size;
  const selectedCustomer = customers.find((customer) => customer.id === form.customerId);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="List details" description="What this price list is and who it is for.">
            <div className={sectionGrid}>
              <Field label="Code" htmlFor="pl-code" required error={errors.code}>
                <input
                  id="pl-code"
                  type="text"
                  value={form.code}
                  onChange={(e) => setField('code', e.target.value)}
                  className={cn(fieldClass('code'), 'font-mono')}
                  placeholder="e.g. PL-2026-STD"
                />
              </Field>

              <Field label="Name" htmlFor="pl-name" required error={errors.name}>
                <input
                  id="pl-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  className={fieldClass('name')}
                  placeholder="e.g. Standard prices 2026"
                />
              </Field>

              <Field
                label="Description"
                htmlFor="pl-description"
                required
                error={errors.description}
                className="sm:col-span-2"
              >
                <textarea
                  id="pl-description"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  className={fieldClass('description')}
                  placeholder="When and why this price list is used"
                />
              </Field>

              <Field label="Type" htmlFor="pl-type">
                <select
                  id="pl-type"
                  value={form.type}
                  onChange={(e) => setField('type', e.target.value)}
                  className={inputClass}
                >
                  {Object.values(PriceListType).map((type) => (
                    <option key={type} value={type}>
                      {PRICE_LIST_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Status"
                htmlFor="pl-status"
                hint="Only an active list is applied to sales orders."
              >
                <select
                  id="pl-status"
                  value={form.status}
                  onChange={(e) => setField('status', e.target.value)}
                  className={inputClass}
                >
                  <option value={PriceListStatus.DRAFT}>Draft</option>
                  <option value={PriceListStatus.ACTIVE}>Active</option>
                  <option value={PriceListStatus.INACTIVE}>Inactive</option>
                </select>
              </Field>

              {isCustomerSpecific && (
                <Field
                  label="Customer"
                  htmlFor="pl-customer"
                  required
                  error={errors.customerId}
                  className="sm:col-span-2"
                  hint="These prices apply only to orders from this customer."
                >
                  <select
                    id="pl-customer"
                    value={form.customerId}
                    onChange={(e) => setField('customerId', e.target.value)}
                    className={fieldClass('customerId')}
                  >
                    <option value="">Select a customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} ({customer.code})
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
          </Section>

          <Section step={2} title="Validity and rules" description="When the list applies and how it competes with others.">
            <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Valid From" htmlFor="pl-valid-from" required error={errors.validFrom}>
                <input
                  id="pl-valid-from"
                  type="date"
                  value={form.validFrom}
                  onChange={(e) => setField('validFrom', e.target.value)}
                  className={fieldClass('validFrom')}
                />
              </Field>

              <Field label="Valid To" htmlFor="pl-valid-to" error={errors.validTo} hint="Leave empty for no end date.">
                <input
                  id="pl-valid-to"
                  type="date"
                  min={form.validFrom}
                  value={form.validTo}
                  onChange={(e) => setField('validTo', e.target.value)}
                  className={fieldClass('validTo')}
                />
              </Field>

              <Field label="Currency" htmlFor="pl-currency">
                <select
                  id="pl-currency"
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value)}
                  className={inputClass}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </Field>

              <Field
                label="Priority"
                htmlFor="pl-priority"
                error={errors.priority}
                hint="When several lists apply, the highest number wins."
              >
                <input
                  id="pl-priority"
                  type="number"
                  min="1"
                  step="1"
                  value={form.priority}
                  onChange={(e) => setField('priority', e.target.value)}
                  className={fieldClass('priority')}
                />
              </Field>

              <Field
                label="List Discount (%)"
                htmlFor="pl-global-discount"
                error={errors.globalDiscountPercent}
                hint="Taken off every item on top of its own price."
                className="lg:col-span-2"
              >
                <input
                  id="pl-global-discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.globalDiscountPercent}
                  onChange={(e) => setField('globalDiscountPercent', e.target.value)}
                  className={fieldClass('globalDiscountPercent')}
                  placeholder="0"
                />
              </Field>
            </div>
          </Section>

          <Section
            step={3}
            title="Product prices"
            description="One row per product; add the same product again for a quantity tier."
            action={
              <button
                type="button"
                onClick={() => setItems((prev) => [...prev, newItem()])}
                className="inline-flex shrink-0 items-center rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Product
              </button>
            }
          >
            <div className="space-y-3 p-4">
              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}

              {items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center">
                  <TagIcon className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm font-medium text-gray-900">No product prices yet</p>
                  <p className="mt-1 text-sm text-gray-500">Add the products this list sets a price for.</p>
                </div>
              ) : (
                items.map((item, index) => {
                  const isFixed = item.priceType === PriceType.FIXED;
                  const finalPrice = computeFinalPrice(item);
                  return (
                    <div key={item.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Price {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                          className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900"
                          title="Remove price"
                          aria-label="Remove price"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-12">
                        <Field
                          label="Product"
                          htmlFor={`pl-item-${index}-product`}
                          required
                          className="col-span-2 sm:col-span-5"
                          error={errors[`item_${index}_productId`]}
                        >
                          <select
                            id={`pl-item-${index}-product`}
                            value={item.productId}
                            onChange={(e) => setItemField(index, 'productId', e.target.value)}
                            className={fieldClass(`item_${index}_productId`)}
                          >
                            <option value="">{productsLoading ? 'Loading products...' : 'Select a product'}</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.sku}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field label="Price Type" htmlFor={`pl-item-${index}-type`} className="col-span-2 sm:col-span-4">
                          <select
                            id={`pl-item-${index}-type`}
                            value={item.priceType}
                            onChange={(e) => setItemField(index, 'priceType', e.target.value)}
                            className={inputClass}
                          >
                            {Object.values(PriceType).map((type) => (
                              <option key={type} value={type}>
                                {PRICE_TYPE_LABELS[type]}
                              </option>
                            ))}
                          </select>
                        </Field>

                        <Field
                          label="Base Price"
                          htmlFor={`pl-item-${index}-base`}
                          required
                          className="sm:col-span-3"
                          error={errors[`item_${index}_basePrice`]}
                        >
                          <input
                            id={`pl-item-${index}-base`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.basePrice}
                            onChange={(e) => setItemField(index, 'basePrice', e.target.value)}
                            className={fieldClass(`item_${index}_basePrice`)}
                            placeholder="0.00"
                          />
                        </Field>

                        {isFixed ? (
                          <Field
                            label="Final Price"
                            htmlFor={`pl-item-${index}-final`}
                            required
                            className="sm:col-span-3"
                            error={errors[`item_${index}_finalPrice`]}
                          >
                            <input
                              id={`pl-item-${index}-final`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.finalPrice}
                              onChange={(e) => setItemField(index, 'finalPrice', e.target.value)}
                              className={fieldClass(`item_${index}_finalPrice`)}
                              placeholder="0.00"
                            />
                          </Field>
                        ) : (
                          <Field
                            label={item.priceType === PriceType.PERCENTAGE_MARKUP ? 'Markup (%)' : 'Discount (%)'}
                            htmlFor={`pl-item-${index}-percentage`}
                            required
                            className="sm:col-span-3"
                            error={errors[`item_${index}_percentage`]}
                          >
                            <input
                              id={`pl-item-${index}-percentage`}
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.percentage}
                              onChange={(e) => setItemField(index, 'percentage', e.target.value)}
                              className={fieldClass(`item_${index}_percentage`)}
                              placeholder="0"
                            />
                          </Field>
                        )}

                        <Field
                          label="Min Qty"
                          htmlFor={`pl-item-${index}-min`}
                          className="sm:col-span-3"
                          error={errors[`item_${index}_minimumQuantity`]}
                        >
                          <input
                            id={`pl-item-${index}-min`}
                            type="number"
                            min="1"
                            step="1"
                            value={item.minimumQuantity}
                            onChange={(e) => setItemField(index, 'minimumQuantity', e.target.value)}
                            className={fieldClass(`item_${index}_minimumQuantity`)}
                          />
                        </Field>

                        <Field
                          label="Max Qty"
                          htmlFor={`pl-item-${index}-max`}
                          className="sm:col-span-3"
                          error={errors[`item_${index}_maximumQuantity`]}
                        >
                          <input
                            id={`pl-item-${index}-max`}
                            type="number"
                            min="1"
                            step="1"
                            value={item.maximumQuantity}
                            onChange={(e) => setItemField(index, 'maximumQuantity', e.target.value)}
                            className={fieldClass(`item_${index}_maximumQuantity`)}
                            placeholder="No limit"
                          />
                        </Field>

                        <Field
                          label="Extra Discount (%)"
                          htmlFor={`pl-item-${index}-extra`}
                          className="sm:col-span-3"
                          error={errors[`item_${index}_additionalDiscountPercent`]}
                        >
                          <input
                            id={`pl-item-${index}-extra`}
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.additionalDiscountPercent}
                            onChange={(e) => setItemField(index, 'additionalDiscountPercent', e.target.value)}
                            className={fieldClass(`item_${index}_additionalDiscountPercent`)}
                            placeholder="0"
                          />
                        </Field>
                      </div>

                      <p className="mt-3 flex justify-end gap-2 border-t border-gray-200 pt-2 text-sm text-gray-600">
                        {isFixed ? 'Price on this list' : 'Calculated price'}
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(finalPrice, form.currency)}
                        </span>
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </Section>
        </div>

        {/* Right: summary and how the list is applied */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Price list</p>
                  <p className="font-medium text-gray-900">{form.name.trim() || 'Not named yet'}</p>
                  {form.code.trim() && <p className="font-mono text-xs text-gray-500">{form.code.trim()}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <p className="text-xs text-gray-500">Prices</p>
                    <p className="text-lg font-semibold text-gray-900 tabular-nums">{items.length}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 px-3 py-2">
                    <p className="text-xs text-gray-500">Products</p>
                    <p className="text-lg font-semibold text-gray-900 tabular-nums">{productCount}</p>
                  </div>
                </div>
                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Applies to</dt>
                    <dd className="text-right font-medium text-gray-900">
                      {isCustomerSpecific ? selectedCustomer?.name || 'A customer (not chosen)' : 'All customers'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Valid</dt>
                    <dd className="text-right font-medium text-gray-900">
                      {form.validFrom || '—'} to {form.validTo || 'no end date'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">List discount</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">
                      {toNumber(form.globalDiscountPercent)}%
                    </dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <h4 className="text-sm font-semibold text-gray-900">How this list is used</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Only an active list inside its dates sets prices on sales orders.</li>
                <li>A customer-specific list beats a general one for that customer.</li>
                <li>Otherwise the list with the highest priority wins.</li>
              </ul>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">
              {items.length} {items.length === 1 ? 'price' : 'prices'} on this list
            </span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving
              ? isEdit
                ? 'Updating...'
                : 'Creating...'
              : isEdit
                ? 'Update Price List'
                : 'Create Price List'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default PriceListForm;
